import React, { Component } from "react";
import { NightScene } from "./Art";
import Wheel from "./Wheel";
import CardTrick from "./CardTrick";
import Riddle from "./Riddle";
import Prize from "./Prize";
import TestBar from "./TestBar";
import {
  HalloweenState,
  Play,
  Prize as PrizeName,
  Trick,
  closing,
  playHalloween,
  replayHalloween,
  trackHalloween,
} from "./api";

// The game itself, the same in the pop-up and on /halloween. Trick or treat;
// treat spins the wheel, trick is one of the witch's two tricks at random;
// both end on the prize. The go is recorded the moment it starts, so closing
// half way through still leaves the visitor their prize next time.

type Stage = "choose" | "wheel" | "cards" | "riddle" | "prize" | "over";

interface Props {
  halloween: HalloweenState;
  variant: "popup" | "page";
  onClose?: () => void;
  // Null when test mode's "play again" has undone the go.
  onPlayed?: (play: Play | null) => void;
}

interface State {
  stage: Stage;
  play: Play | null;
  returning: boolean;
  target?: number;
  busy: boolean;
  error: string;
  // Test mode's choices, and its "play again" in flight.
  testPrize: PrizeName | "";
  testTrick: Trick | "";
  replaying: boolean;
}

const firstStage = (halloween: HalloweenState): Stage => {
  if (halloween.play) return "prize";
  return halloween.running ? "choose" : "over";
};

class TrickOrTreat extends Component<Props, State> {
  private gameRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);
    this.state = {
      stage: firstStage(props.halloween),
      play: props.halloween.play,
      returning: !!props.halloween.play,
      busy: false,
      error: "",
      testPrize: "",
      testTrick: "",
      replaying: false,
    };
  }

  // Each step starts at its top: on a phone the prize could otherwise arrive
  // below the fold with the visitor looking at the bottom of the wheel.
  componentDidUpdate(_: Props, previous: State) {
    if (previous.stage === this.state.stage) return;

    const game = this.gameRef.current;
    if (!game) return;

    if (this.props.variant === "popup") {
      const card = game.closest(".hw-popup__card");
      if (card) card.scrollTop = 0;
      return;
    }

    // Clear of the site's sticky navigation bar.
    const top = game.getBoundingClientRect().top + window.pageYOffset - 80;
    if (game.getBoundingClientRect().top < 80) window.scrollTo({ top, behavior: "smooth" });
  }

  failed = (error) => {
    this.setState({
      busy: false,
      error: error?.response?.data?.error || "The cauldron hiccupped. Give it another go in a moment.",
    });
  };

  // An earlier go came back instead of a new one: skip straight to it.
  showEarlier = (play: Play) => {
    this.setState({ play, returning: true, busy: false, stage: "prize" });
    this.props.onPlayed?.(play);
  };

  chooseTreat = () => {
    trackHalloween("choose", { path: "treat" });
    this.setState({ stage: "wheel", error: "" });
  };

  chooseTrick = () => {
    const trick: Trick = this.state.testTrick || (Math.random() < 0.5 ? "cards" : "riddle");
    trackHalloween("choose", { path: "trick", trick });
    this.setState({ busy: true, error: "" });

    playHalloween("trick", trick)
      .then(({ play, fresh }) => {
        if (!fresh) return this.showEarlier(play);

        trackHalloween("play", { path: "trick", trick: play.trick, prize: play.prize });
        this.setState({ play, busy: false, stage: play.trick === "riddle" ? "riddle" : "cards" });
        this.props.onPlayed?.(play);
      })
      .catch(this.failed);
  };

  spin = () => {
    this.setState({ busy: true, error: "" });

    playHalloween("treat", undefined, this.state.testPrize || undefined)
      .then(({ play, slice, fresh }) => {
        if (!fresh || slice === null) return this.showEarlier(play);

        trackHalloween("play", { path: "treat", prize: play.prize });
        // The prize stays hidden until the wheel has stopped on it.
        this.setState({ play, target: slice });
        this.props.onPlayed?.(play);
      })
      .catch(this.failed);
  };

  // Test mode: undo the go on the server and start again from the top.
  replay = () => {
    this.setState({ replaying: true, error: "" });
    replayHalloween()
      .then(() => {
        this.setState({ stage: "choose", play: null, returning: false, target: undefined, busy: false, replaying: false });
        this.props.onPlayed?.(null);
      })
      .catch((error) => {
        this.setState({ replaying: false });
        this.failed(error);
      });
  };

  renderTestBar() {
    const { halloween } = this.props;
    const { stage, testPrize, testTrick, replaying } = this.state;
    if (!halloween.tester || !halloween.running) return null;
    if (stage !== "choose" && stage !== "prize") return null;

    return (
      <TestBar
        prizes={halloween.prizes}
        prize={testPrize}
        trick={testTrick}
        onPrize={(prize) => this.setState({ testPrize: prize })}
        onTrick={(trick) => this.setState({ testTrick: trick })}
        onReplay={stage === "prize" ? this.replay : undefined}
        replaying={replaying}
      />
    );
  }

  showPrize = () => {
    trackHalloween("prize", { prize: this.state.play?.prize });
    this.setState({ stage: "prize", busy: false });
  };

  renderStage() {
    const { halloween, onClose } = this.props;
    const { stage, play, busy, error } = this.state;

    switch (stage) {
      case "over":
        return (
          <div className="hw-choose">
            <NightScene />
            <p className="hw-eyebrow">Halloween at 42courses</p>
            <h2>The cauldron has gone cold.</h2>
            <p className="hw-lead">Halloween's over for this year, but every course is still here.</p>
            <div className="hw-actions">
              <a className="hw-btn hw-btn--orange" href="/courses">
                See the courses
              </a>
            </div>
          </div>
        );
      case "wheel":
        return (
          <div className="hw-spin">
            <p className="hw-eyebrow">Treat</p>
            <h2>
              42 pumpkins.
              <br />
              One is <span className="hw-golden">golden</span>.
            </h2>
            <Wheel
              wheel={halloween.wheel}
              prizes={halloween.prizes}
              target={this.state.target}
              onStopped={this.showPrize}
            />
            <button className="hw-btn hw-btn--orange hw-btn--big" type="button" onClick={this.spin} disabled={busy}>
              {busy ? "Spinning…" : "Spin the wheel"}
            </button>
            {error && (
              <p className="hw-error" role="alert">
                {error}
              </p>
            )}
          </div>
        );
      case "cards":
        return <CardTrick onDone={this.showPrize} />;
      case "riddle":
        return <Riddle onDone={this.showPrize} />;
      case "prize":
        return play ? (
          <Prize
            play={play}
            endsAt={halloween.ends_at}
            guides={halloween.guides}
            returning={this.state.returning}
            onPlayChanged={(changed) => {
              this.setState({ play: changed });
              // The pop-up keeps the latest play for when it reopens, so a
              // chosen booklet must reach it too.
              this.props.onPlayed?.(changed);
            }}
            onClose={onClose}
          />
        ) : null;
      default:
        return (
          <div className="hw-choose">
            <NightScene />
            <p className="hw-eyebrow">Halloween at 42courses</p>
            <h2>
              Trick or <em>treat?</em>
            </h2>
            <p className="hw-lead">Every trick ends in a treat.</p>
            {/* In the order of the saying (Chris, 27 Oct 2026): trick, then treat. */}
            <div className="hw-choices">
              <button className="hw-btn hw-btn--lavender hw-btn--big" type="button" onClick={this.chooseTrick} disabled={busy}>
                {busy ? "Summoning…" : "Trick"}
              </button>
              <button className="hw-btn hw-btn--orange hw-btn--big" type="button" onClick={this.chooseTreat} disabled={busy}>
                Treat
              </button>
            </div>
            {error && (
              <p className="hw-error" role="alert">
                {error}
              </p>
            )}
            <p className="hw-small">
              One go each.{closing(halloween.ends_at) && ` Ends ${closing(halloween.ends_at)?.words}.`}
            </p>
          </div>
        );
    }
  }

  render() {
    return (
      <div ref={this.gameRef} className={`hw-game hw-game--${this.props.variant} hw-stage--${this.state.stage}`}>
        <div className="hw-game__bar">
          <img
            className="hw-game__logo"
            src={require("../../../../assets/images/facelift/logos/light_rectangle.svg")}
            alt="42courses"
          />
          {this.props.onClose && (
            <button className="hw-game__close" type="button" aria-label="Close" onClick={this.props.onClose}>
              &times;
            </button>
          )}
        </div>
        {this.renderStage()}
        {this.renderTestBar()}
      </div>
    );
  }
}

export default TrickOrTreat;
